import { AfterViewInit, Component, OnDestroy, input, output, viewChild, effect, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridStack, GridStackOptions, GridStackWidget, GridStackNode } from 'gridstack';
import { GridstackComponent, GridstackModule, elementCB, nodesCB } from 'gridstack/dist/angular';
import { DynamicModule } from 'ng-dynamic-component';
import { ButtonModule } from 'primeng/button';
import { DashboardWorkspaceModel, UiComponentModel } from '../../models/dashboard.model';
import { UserRoles } from '../../../user-admin-page/user-model';
import { ComponentLauncher } from '../../services/component-launcher';

@Component({
  selector: 'app-workspace-gridstack',
  templateUrl: './workspace-gridstack.component.html',
  styles: [':host { display: block; height: 100%; overflow-y: auto; }'],
  standalone: true,
  imports: [CommonModule, GridstackModule, DynamicModule, ButtonModule]
})
export class WorkspaceGridstackComponent implements AfterViewInit, OnDestroy {

  readonly workspace = input.required<DashboardWorkspaceModel>();
  readonly userRoles = input<UserRoles | null>(null);
  readonly currentStringName = input('');
  readonly holeSectionName = input('');
  readonly componentInstance = input('');
  readonly compactMode = input(false);
  readonly compactRequestVersion = input(0);
  readonly gridOptions = input<GridStackOptions>({});
  readonly stringDependentComponents = input<string[]>([]);
  readonly scrollToItemId = input<string | null>(null);

  readonly componentRemoved = output<UiComponentModel>();
  readonly gridChanged = output<nodesCB>();
  readonly gridItemResized = output<elementCB>();
  readonly gridItemAdded = output<nodesCB>();

  private readonly _gridstack = viewChild(GridstackComponent);
  private readonly _widgetCache = new Map<string, GridStackWidget>();
  private readonly _sizeConstraints = new Map<string, { minW?: number; maxW?: number; minH?: number; maxH?: number }>();
  private _lastProcessedCompactRequest = 0;
  private _lastAppliedCompactMode: boolean | null = null;
  private _isCompacting = false;
  private _isDragging = false;
  private _activeGridItemId: string | null = null;
  private _draggingGridItemId: string | null = null;

  constructor(componentLauncher: ComponentLauncher, private _zone: NgZone) {
    const registry = componentLauncher.getComponentRegistry();
    registry.forEach((c: any) => {
      const constraint: { minW?: number; maxW?: number; minH?: number; maxH?: number } = {};
      if (c.minW != null) constraint.minW = c.minW;
      if (c.maxW != null) constraint.maxW = c.maxW;
      if (c.minH != null) constraint.minH = c.minH;
      if (c.maxH != null) constraint.maxH = c.maxH;
      if (Object.keys(constraint).length) this._sizeConstraints.set(c.name, constraint);
    });

    // Run compact only when explicitly requested by the parent.
    effect(() => {
      const requestVersion = this.compactRequestVersion();
      const grid = this._gridstack()?.grid;
      if (!grid || requestVersion === 0 || requestVersion === this._lastProcessedCompactRequest) {
        return;
      }
      this._lastProcessedCompactRequest = requestVersion;
      this.compactGrid(grid);
    });

    effect(() => {
      const compactMode = this.compactMode();
      const grid = this._gridstack()?.grid;
      if (!grid || compactMode === this._lastAppliedCompactMode) {
        return;
      }

      this._lastAppliedCompactMode = compactMode;
      grid.float(!compactMode);
      if (compactMode) {
        this.compactGrid(grid);
      }
    });

    effect(() => {
      const id = this.scrollToItemId();
      if (!id) {
        return;
      }
      this.scrollItemIntoView(id);
    });
  }

  private scrollItemIntoView(id: string): void {
    const tryScroll = (attempt: number): void => {
      const grid = this._gridstack()?.grid;
      const node = grid?.engine.nodes.find(n => String(n.id) === String(id));
      const el = node?.el as HTMLElement | undefined;
      if (!el) {
        if (attempt < 10) requestAnimationFrame(() => tryScroll(attempt + 1));
        return;
      }
      const scroller = this.findScrollContainer(el);
      if (!scroller) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const elRect = el.getBoundingClientRect();
      const scRect = scroller.getBoundingClientRect();
      const target = scroller.scrollTop + (elRect.top - scRect.top) - (scRect.height - elRect.height) / 2;
      scroller.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    };
    requestAnimationFrame(() => tryScroll(0));
  }

  private findScrollContainer(el: HTMLElement): HTMLElement | null {
    let cur: HTMLElement | null = el.parentElement;
    while (cur) {
      const style = getComputedStyle(cur);
      if (/(auto|scroll)/.test(style.overflowY) && cur.scrollHeight > cur.clientHeight) {
        return cur;
      }
      cur = cur.parentElement;
    }
    return null;
  }

  private readonly _onWindowResize = (): void => {
    const grid = this._gridstack()?.grid;
    if (!grid) return;
    // Recompute square cell height based on current cell width, same as gridster's computeGridColWidth().
    // Using window.resize (not ResizeObserver) means scrollbar appearance never triggers this.
    grid.cellHeight(grid.cellWidth());
  };

  ngAfterViewInit(): void {
    // The parent only renders this component after its IDB load completes (panelModeLoaded guard),
    // so the container already has real dimensions by the time gridstack initializes.
    this._gridstack()?.grid?.onResize();
    window.addEventListener('resize', this._onWindowResize);
  }

  toGridItem(item: UiComponentModel): GridStackWidget {
    const cached = this._widgetCache.get(item.id);
    if (cached && cached.x === (item.x ?? 0) && cached.y === (item.y ?? 0) && cached.w === item.cols && cached.h === item.rows) {
      return cached;
    }
    const constraints = this._sizeConstraints.get(item.name) ?? {};
    const widget: GridStackWidget = { id: item.id, x: item.x ?? 0, y: item.y ?? 0, w: item.cols, h: item.rows, ...constraints };
    this._widgetCache.set(item.id, widget);
    return widget;
  }

  onGridChange(data: nodesCB): void {
    if (this._isCompacting) {
      return;
    }

    const grid = this._gridstack()?.grid;
    if (grid && this.compactMode() && !this._isDragging) {
      // After drop/change, settle overlaps without pushing the dragged item.
      const changedNodeId = data.nodes?.find(n => n?.id != null)?.id;
      const preserveId = changedNodeId != null
        ? String(changedNodeId)
        : (this._draggingGridItemId ?? this._activeGridItemId);
      this.compactGrid(grid, 'settle', preserveId);
      return;
    }

    this._zone.run(() => {
      const ws = this.workspace();
      data.nodes.forEach((node: GridStackNode) => {
        const item = ws.uiComponents.find(c => c.id === node.id);
        if (item) {
          this.syncNodeToItem(item, node);
          if (node.id) this._widgetCache.delete(node.id);
        }
      });
      this.gridChanged.emit({ nodes: [...data.nodes] } as nodesCB);
    });
  }

  onGridItemResized(data: elementCB): void {
    const grid = this._gridstack()?.grid;
    if (grid && this.compactMode()) {
      const resizedNodeId = data?.el?.gridstackNode?.id;
      const preserveId = resizedNodeId != null ? String(resizedNodeId) : null;
      this.compactGrid(grid, 'full', preserveId);
    }

    // resizeStopCB emits once at end of resize; keep this event for consumers
    this.gridItemResized.emit(data);
  }

  onGridItemAdded(data: { event: Event; nodes: GridStackNode[] }): void {
    this.gridItemAdded.emit({ nodes: data.nodes } as nodesCB);
  }

  onGridDragStart(event?: any): void {
    this._isDragging = true;
    const eventNodeId = event?.node?.id ?? event?.el?.gridstackNode?.id;
    this._draggingGridItemId = eventNodeId != null ? String(eventNodeId) : this._activeGridItemId;
  }

  onGridDragStop(event?: any): void {
    this._isDragging = false;
    const grid = this._gridstack()?.grid;
    if (grid && this.compactMode()) {
      // Full compaction (including left pass) on release so widgets snap into
      // place even when dropped in open space. Pass preserveId so the dropped
      // widget is skipped during resolveOverlaps and not pushed down.
      const eventNodeId = event?.node?.id ?? event?.el?.gridstackNode?.id;
      const preserveId = eventNodeId != null
        ? String(eventNodeId)
        : (this._draggingGridItemId ?? this._activeGridItemId);
      this.compactGrid(grid, 'full', preserveId);
    }
    this._draggingGridItemId = null;
  }

  onGridItemRemoved(): void {
    const grid = this._gridstack()?.grid;
    if (!grid || !this.compactMode()) return;
    this.compactGrid(grid);
  }

  private compactGrid(grid: GridStack, mode: 'full' | 'vertical' | 'settle' = 'full', preserveNodeId?: string | null): void {
    if (this._isCompacting) {
      return;
    }

    this._isCompacting = true;
    try {
      if (mode === 'full') {
        this.compactGridLeft(grid, preserveNodeId ?? null);
      } else if (mode === 'settle') {
        this.compactGridSettle(grid, preserveNodeId ?? null);
      } else {
        this.compactGridVertical(grid);
      }
      const ws = this.workspace();
      grid.engine.nodes.forEach((node: GridStackNode) => {
        const item = ws.uiComponents.find(c => c.id === node.id);
        if (item) {
          this.syncNodeToItem(item, node);
          if (node.id) this._widgetCache.delete(node.id);
        }
      });
      this.gridChanged.emit({ nodes: [...grid.engine.nodes] } as nodesCB);
    } finally {
      this._isCompacting = false;
    }
  }

  private compactGridVertical(grid: GridStack): void {
    grid.batchUpdate();
    try {
      this.compactGridUp(grid);
    } finally {
      grid.batchUpdate(false);
    }
  }

  private compactGridSettle(grid: GridStack, preserveNodeId: string | null): void {
    const nodes = [...grid.engine.nodes]
      .filter((n): n is GridStackNode & { x: number; y: number; w: number; h: number } =>
        typeof n.x === 'number' && typeof n.y === 'number' && typeof n.w === 'number' && typeof n.h === 'number')
      .sort((a, b) => a.y - b.y || a.x - b.x);

    grid.batchUpdate();
    try {
      this.resolveOverlaps(grid, nodes, preserveNodeId);
      this.compactGridUp(grid);
    } finally {
      grid.batchUpdate(false);
    }
  }

  private compactGridLeft(grid: GridStack, preserveNodeId: string | null = null): void {
    const nodes = [...grid.engine.nodes]
      .filter((n): n is GridStackNode & { x: number; y: number; w: number; h: number } =>
        typeof n.x === 'number' && typeof n.y === 'number' && typeof n.w === 'number' && typeof n.h === 'number')
      .sort((a, b) => a.y - b.y || a.x - b.x);

    grid.batchUpdate();
    try {
      // Step 1: Move left
      nodes.forEach(node => {
        let targetX = node.x;
        while (targetX > 0 && !grid.engine.collide(node, { ...node, x: targetX - 1 })) {
          targetX -= 1;
        }

        if (targetX !== node.x) {
          if (node.el) {
            grid.update(node.el, { x: targetX, y: node.y, w: node.w, h: node.h });
          } else {
            node.x = targetX;
          }
        }
      });

      // Step 2: Move down to resolve overlaps (skip the just-dropped widget so
      // it is not pushed down by the post-drop compaction pass)
      this.resolveOverlaps(grid, nodes, preserveNodeId);

      // Step 3: Move up to fill vertical gaps — use live engine nodes so positions
      // reflect the updates already applied in steps 1 & 2.
      this.compactGridUp(grid);
    } finally {
      grid.batchUpdate(false);
    }
  }

  private resolveOverlaps(
    grid: GridStack,
    nodes: Array<GridStackNode & { x: number; y: number; w: number; h: number }>,
    preserveNodeId: string | null = null
  ): void {
    nodes.forEach(node => {
      if (preserveNodeId && String(node.id) === preserveNodeId) {
        return;
      }

      let targetY = node.y;
      let guard = 0;
      while (grid.engine.collide(node, { ...node, y: targetY }) && guard < 500) {
        targetY += 1;
        guard += 1;
      }

      if (targetY !== node.y) {
        if (node.el) {
          grid.update(node.el, { x: node.x, y: targetY, w: node.w, h: node.h });
        } else {
          node.y = targetY;
        }
      }
    });
  }

  /** Pull every node up as far as it can go without colliding, using live engine positions. */
  private compactGridUp(grid: GridStack): void {
    // Sort by live Y so topmost nodes get priority and don't push things below them.
    const liveNodes = [...grid.engine.nodes]
      .filter((n): n is GridStackNode & { x: number; y: number; w: number; h: number } =>
        typeof n.x === 'number' && typeof n.y === 'number' && typeof n.w === 'number' && typeof n.h === 'number')
      .sort((a, b) => a.y - b.y || a.x - b.x);

    liveNodes.forEach(node => {
      let targetY = node.y;
      // Use a separate rect so we never mutate the live node during collision probing.
      const testRect = { x: node.x, y: targetY, w: node.w, h: node.h };
      while (targetY > 0) {
        testRect.y = targetY - 1;
        if (grid.engine.collide(node, testRect)) {
          break;
        }
        targetY -= 1;
      }

      if (targetY !== node.y) {
        if (node.el) {
          grid.update(node.el, { x: node.x, y: targetY, w: node.w, h: node.h });
        } else {
          node.y = targetY;
        }
      }
    });
  }

  private syncNodeToItem(item: UiComponentModel, node: GridStackNode): void {
    // GridStack can emit transient nodes without finalized coordinates during lifecycle transitions.
    // Preserve last known persisted coordinates instead of overwriting with undefined.
    if (typeof node.x === 'number') {
      item.x = node.x;
    }
    if (typeof node.y === 'number') {
      item.y = node.y;
    }
    if (typeof node.w === 'number') {
      item.cols = node.w;
    }
    if (typeof node.h === 'number') {
      item.rows = node.h;
    }
  }

  getActiveComponent(id: string): void { this._activeGridItemId = id; }
  clearActiveComponent(): void { this._activeGridItemId = null; }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this._onWindowResize);
    this._widgetCache.clear();
  }
}
